import { useLazyQuery, useQuery } from "@apollo/client/react";
import moment from "moment";
import { useEffect, useState } from "react";
import { adaptDelegacionLiquidacionArray } from "../../../adapters/delegacionAdapter";
import { GET_TIPO_PROFESIONALES } from "../../../data/graphQL/queries/AdminRoles/getTipoProfesionales";
import { GET_DELEGACION_PRELIQUIDACION } from "../../../data/graphQL/queries/PreLiquidacion/getDelegacionPreLiquidacion";
import {
    GET_PROFESIONALES_PARA_LIQUIDAR,
    adaptProfesionalesParaLiquidarArray
} from "../../../data/graphQL/queries/PreLiquidacion/getProfesionalesParaLiquidar";
import { usePersistFilters } from "../../../hooks/usePersistFilters";

const idUsuarioLoggeado = sessionStorage.getItem('idUsuario');

const ESTADOS_PRE_LIQUIDACION = [
    { id: 1, displayName: 'Pendiente' },
    { id: 2, displayName: 'Aprobado' },
    { id: 3, displayName: 'Desestimado' }
];

const ESTADOS_LIQUIDACION_FINAL = [
    { id: 1, displayName: 'Pendiente' },
    { id: 4, displayName: 'Aprobado' },
    { id: 3, displayName: 'Rechazado' }
];

export const useGetFilters = (setIsRefetchRequired, liquidacionFinal) => {
    const [delegaciones, setDelegaciones] = useState([]);
    const [profesionales, setProfesionales] = useState([]);
    const [tiposProfesional, setTiposProfesional] = useState([]);

    const { filters, handleChangeFilters: handleChangeFiltersBase } = usePersistFilters({
        mes: moment().month() + 1,
        anio: moment().year(),
        idTipoProfesional: null,
        idDelegacion: null,
        idsProfesionales: [],
        idEstado: 1,
        filtrar: ''
    }, liquidacionFinal ? "preLiquidacionFilters" : "liquidacionFinalFilters");

    const handleChangeFilters = (name, value) => {
        if (name === 'idDelegacion') {
            handleChangeFiltersBase('idsProfesionales', []);
        }
        if (name === 'idTipoProfesional') {
            handleChangeFiltersBase('idsProfesionales', []);
            handleChangeFiltersBase('idDelegacion', null);
        }
        handleChangeFiltersBase(name, value);

        if (name !== 'idsProfesionales' && name !== 'filtrar') {
            setIsRefetchRequired(true);
        }
    };

    // ── Tipos de Profesional ─────────────────────────────────────────────────

    const { data: tipoProfesionalData } = useQuery(GET_TIPO_PROFESIONALES);

    useEffect(() => {
        if (!tipoProfesionalData) return;

        const tipoProfesionalOptions = tipoProfesionalData?.tipoProfesionales?.valorEsperado || [];
        setTiposProfesional(
            tipoProfesionalOptions.map(x => ({
                id: x.idTipoProfesional,
                displayName: x.descripcion
            }))
        );
    }, [tipoProfesionalData]);

    // ── Delegaciones ─────────────────────────────────────────────────────────

    const [getDelegaciones, { data: dataDelegaciones }] = useLazyQuery(
        GET_DELEGACION_PRELIQUIDACION,
        { fetchPolicy: 'no-cache' }
    );

    useEffect(() => {
        if (!filters.idTipoProfesional) return;
        getDelegaciones({ variables: { idTipoProfesional: filters.idTipoProfesional, mes: filters.mes, anio: filters.anio } });
    }, [getDelegaciones, filters.idTipoProfesional, filters.mes, filters.anio]);

    useEffect(() => {
        if (!dataDelegaciones) return;

        const adaptadas = adaptDelegacionLiquidacionArray(dataDelegaciones)?.filter(x => x.id !== null) ?? [];
        setDelegaciones(adaptadas);
    }, [dataDelegaciones]);

    // ── Profesionales (depende de tipo + delegacion) ────────────────────────

    const [getProfesionales, { data: dataProfesionales }] = useLazyQuery(
        GET_PROFESIONALES_PARA_LIQUIDAR,
        { fetchPolicy: 'no-cache' }
    );

    useEffect(() => {
        if (!filters.idTipoProfesional || !filters.idDelegacion) {
            setProfesionales([]);
            return;
        }

        getProfesionales({
            variables: {
                idTipoProfesional: filters.idTipoProfesional,
                idDelegacion: filters.idDelegacion,
                mes: filters.mes,
                anio: filters.anio,
                idEstado: filters.idEstado,
                liquidacionFinal
            }
        });
    }, [filters.idTipoProfesional, filters.idDelegacion, filters.mes, filters.anio, filters.idEstado, getProfesionales]);

    useEffect(() => {
        if (!dataProfesionales) return;

        const adaptados = adaptProfesionalesParaLiquidarArray(dataProfesionales);
        setProfesionales(
            adaptados.map(p => ({
                id: p.id,
                displayName: `${p.apellido}, ${p.nombre}`
            }))
        );
    }, [dataProfesionales]);

    const estados = liquidacionFinal ? ESTADOS_LIQUIDACION_FINAL : ESTADOS_PRE_LIQUIDACION;

    return {
        filters,
        handleChangeFilters,
        delegaciones,
        profesionales,
        estados,
        tiposProfesional,
    };
};